// src/components/CollapsibleSection.ts
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('collapsible-section')
export class CollapsibleSection extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) title: string = '';
  @property({ type: String }) icon: string = '';
  @property({ type: Boolean }) open: boolean = true;
  @property({ type: String }) description: string = '';

  @state() private isOpen: boolean = true;

  connectedCallback(): void {
    super.connectedCallback();
    this.isOpen = this.open;
  }

  updated(changedProperties: Map<string, any>) {
    if (changedProperties.has('open')) {
      this.isOpen = this.open;
    }
  }

  private toggle() {
    this.isOpen = !this.isOpen;
    this.dispatchEvent(new CustomEvent('toggle', {
      detail: { open: this.isOpen },
      bubbles: true,
      composed: true
    }));
  }

  render() {
    return html`
      <style>
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      </style>

      <div class="collapsible-wrapper rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden
                  transition-all duration-300 ease-out
                  ${this.isOpen ? 'shadow-lg shadow-indigo-500/10' : 'hover:border-indigo-400/30'}">
        <!-- Header -->
        <button
          @click=${this.toggle}
          class="w-full flex items-center justify-between gap-3 p-4 cursor-pointer
                 group hover:bg-white/5 transition-all duration-200
                 ${this.isOpen ? '' : 'hover:bg-indigo-500/5'}"
        >
          <div class="flex items-center gap-3 flex-1 min-w-0">
            ${this.icon ? html`
              <div class="flex-shrink-0 text-2xl transition-transform duration-300 ease-out
                          ${this.isOpen ? 'scale-110' : 'scale-100 group-hover:scale-110'}">
                ${this.icon}
              </div>
            ` : null}
            <div class="flex-1 text-left min-w-0">
              <h3 class="text-base font-semibold text-slate-100 truncate
                         transition-colors duration-200
                         ${this.isOpen ? 'text-indigo-100' : 'group-hover:text-indigo-200'}">
                ${this.title}
              </h3>
              ${this.description ? html`
                <p class="text-xs text-slate-400 mt-0.5 truncate">${this.description}</p>
              ` : null}
            </div>
          </div>

          <!-- Animated chevron -->
          <div class="flex-shrink-0 transition-all duration-300 ease-out
                      ${this.isOpen ? 'rotate-180 text-indigo-400' : 'rotate-0 text-slate-400 group-hover:text-indigo-300'}">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </div>
        </button>

        <!-- Content wrapper -->
        <div class="overflow-hidden transition-all duration-300 ease-out
                    ${this.isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}">
          <div class="content-slot p-4 pt-0 border-t border-white/5
                      ${this.isOpen ? 'animate-fadeIn' : ''}">
            <!-- Children will be moved here in firstUpdated() -->
          </div>
        </div>
      </div>
    `;
  }

  firstUpdated() {
    // Find the content slot
    const slot = this.querySelector('.content-slot');
    if (!slot) return;

    // Find the wrapper (our template)
    const wrapper = this.querySelector('.collapsible-wrapper');

    // Get all direct children that aren't our wrapper
    const children = Array.from(this.childNodes).filter(
      node => node !== wrapper
    );

    // Move them into the content slot (one-time operation)
    children.forEach(child => slot.appendChild(child));
  }
}
